import { useState } from 'react';
import { Key, Shield, User } from 'lucide-react';
import { authenticatedFetch, getUser } from '../lib/auth';
import DeviceManagement from './DeviceManagement';

type Tab = 'password' | 'devices';

export default function UserProfile() {
  const user = getUser();
  const [activeTab, setActiveTab] = useState<Tab>('devices');

  // Password change state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changing, setChanging] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleChangePassword = async () => {
    setMessage(null);

    if (!oldPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: 'Vennligst fyll ut alle feltene' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Nye passord samsvarer ikke' });
      return;
    }

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Nytt passord må være minst 8 tegn' });
      return;
    }

    setChanging(true);

    try {
      const response = await authenticatedFetch('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          user_id: user?.id,
          old_password: oldPassword,
          new_password: newPassword,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setMessage({ type: 'success', text: 'Passord endret! Du vil bli logget ut om 3 sekunder...' });
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        // Log out user after password change
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      } else {
        setMessage({ type: 'error', text: result.error || 'Kunne ikke endre passord' });
      }
    } catch (error) {
      console.error('Password change error:', error);
      setMessage({ type: 'error', text: 'En feil oppstod ved endring av passord' });
    } finally {
      setChanging(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-kf-primary rounded-full flex items-center justify-center">
            <User size={32} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Min profil</h1>
            <p className="text-gray-600">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="border-b">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('devices')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'devices'
                  ? 'border-kf-primary text-kf-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Shield size={18} />
              Mine enheter
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'password'
                  ? 'border-kf-primary text-kf-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Key size={18} />
              Endre passord
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'devices' && <DeviceManagement />}

      {activeTab === 'password' && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Endre passord</h2>

          {message && (
            <div className={`mb-4 p-4 rounded-md ${
              message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              {message.text}
            </div>
          )}

          <div className="max-w-md space-y-4">
            <div>
              <label htmlFor="oldPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Nåværende passord
              </label>
              <input
                id="oldPassword"
                type="password"
                className="input"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                placeholder="Skriv inn nåværende passord"
              />
            </div>
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Nytt passord
              </label>
              <input
                id="newPassword"
                type="password"
                className="input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minst 8 tegn"
              />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Bekreft nytt passord
              </label>
              <input
                id="confirmPassword"
                type="password"
                className="input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Skriv inn nytt passord igjen"
              />
            </div>
            <div className="pt-4">
              <button
                onClick={handleChangePassword}
                disabled={changing}
                className="btn-primary"
              >
                {changing ? 'Endrer...' : 'Endre passord'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
